/**
 * ConfigGenerator - Converts maze layout to gallery configuration
 * Assigns videos to painting slots and generates final config
 */

export class ConfigGenerator {
  /**
   * Generate complete gallery config from maze layout and videos
   */
  static generateFromVideos(videoList, mazeLayout, settings = {}) {
    const { walls, paintingSlots, startPosition } = mazeLayout;

    // Assign videos to painting slots
    const paintings = this.assignVideosToSlots(videoList, paintingSlots, settings);

    // Build final configuration
    const config = {
      startPosition: startPosition,
      walls: walls,
      paintings: paintings,
      metadata: {
        generatedAt: new Date().toISOString(),
        seed: mazeLayout.seed,
        gridSize: mazeLayout.gridSize,
        totalSize: mazeLayout.totalSize,
        paintingCount: paintings.length,
        wallCount: walls.length
      }
    };

    console.log('Generated configuration:', {
      paintings: config.paintings.length,
      walls: config.walls.length,
      gridSize: config.metadata.gridSize
    });

    return config;
  }

  /**
   * Assign videos to painting slots
   */
  static assignVideosToSlots(videoList, paintingSlots, settings) {
    const paintings = [];
    const defaultScale = settings.defaultScale || { width: 2, height: 3.52 };

    // Shuffle video list for variety if desired
    const shuffledVideos = settings.shuffleVideos
      ? this.shuffleArray([...videoList], settings.seed)
      : videoList;

    for (let i = 0; i < paintingSlots.length; i++) {
      const slot = paintingSlots[i];
      const video = shuffledVideos[i % shuffledVideos.length]; // Cycle through videos if more slots than videos

      const painting = this.createPaintingConfig(video, slot, i, defaultScale);
      paintings.push(painting);
    }

    return paintings;
  }

  /**
   * Create painting configuration from video and slot
   */
  static createPaintingConfig(video, slot, index, defaultScale) {
    return {
      id: `painting-${index + 1}`,
      title: video.title || `Artwork ${index + 1}`,
      description: video.description || 'Untitled',
      videoUrl: video.videoUrl || video.filename || video.url,
      position: slot.position,
      rotation: slot.rotation,
      scale: video.scale || defaultScale,
      frameStyle: slot.frameStyle
    };
  }

  /**
   * Load video manifest from JSON file
   */
  static async loadVideoManifest(manifestPath = '/videos/manifest.json') {
    try {
      const response = await fetch(manifestPath);
      if (!response.ok) {
        throw new Error(`Failed to load video manifest: ${response.statusText}`);
      }

      const manifest = await response.json();
      console.log(`Loaded ${manifest.videos.length} videos from manifest`);

      // Convert manifest entries to full URLs
      return manifest.videos.map(video => ({
        ...video,
        videoUrl: video.videoUrl || `/videos/${video.filename}`
      }));
    } catch (error) {
      console.error('Error loading video manifest:', error);
      throw error;
    }
  }

  /**
   * Shuffle array using seeded random (for reproducibility)
   */
  static shuffleArray(array, seed = Date.now()) {
    const rng = this.createSeededRNG(seed);
    const shuffled = [...array];

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  /**
   * Seeded random number generator (same as MazeGenerator)
   */
  static createSeededRNG(seed) {
    return function() {
      let t = seed += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /**
   * Export configuration to JSON string
   */
  static exportToJSON(config, pretty = true) {
    return JSON.stringify(config, null, pretty ? 2 : 0);
  }

  /**
   * Validate configuration structure
   */
  static validateConfig(config) {
    const errors = [];

    // Check required fields
    if (!config.startPosition) {
      errors.push('Missing startPosition');
    }
    if (!config.walls || !Array.isArray(config.walls)) {
      errors.push('Missing or invalid walls array');
    }
    if (!config.paintings || !Array.isArray(config.paintings)) {
      errors.push('Missing or invalid paintings array');
    }

    // Validate paintings
    if (config.paintings) {
      config.paintings.forEach((painting, index) => {
        if (!painting.id) errors.push(`Painting ${index} missing id`);
        if (!painting.videoUrl) errors.push(`Painting ${index} missing videoUrl`);
        if (!painting.position) errors.push(`Painting ${index} missing position`);
        if (!painting.rotation) errors.push(`Painting ${index} missing rotation`);
      });
    }

    // Validate walls
    if (config.walls) {
      config.walls.forEach((wall, index) => {
        if (!wall.position) errors.push(`Wall ${index} missing position`);
        if (!wall.rotation) errors.push(`Wall ${index} missing rotation`);
        if (!wall.dimensions) errors.push(`Wall ${index} missing dimensions`);
      });
    }

    if (errors.length > 0) {
      console.error('Configuration validation errors:', errors);
      return false;
    }

    console.log('✓ Configuration valid');
    return true;
  }

  /**
   * Generate fallback video list (when manifest not available)
   */
  static generateFallbackVideoList(count = 9) {
    const videos = [];
    const titles = [
      'Abstract Motion', 'Digital Dreams', 'Void Echoes',
      'Temporal Flux', 'Neon Reverie', 'Cyber Garden',
      'Infinite Loop', 'Data Stream', 'Virtual Horizon',
      'Pixel Paradise', 'Code Poetry', 'Synthetic Soul'
    ];

    for (let i = 0; i < count; i++) {
      videos.push({
        filename: `video-${i + 1}.mp4`,
        videoUrl: `/videos/video-${i + 1}.mp4`,
        title: titles[i % titles.length],
        description: `Generative artwork #${i + 1}`,
        scale: { width: 2, height: 3.52 }
      });
    }

    return videos;
  }
}
